import { useEffect, useState, type ReactNode } from 'react';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { useViewerLocale } from '@/hooks/useViewerLocale';
import useTranslations from '@/hooks/useTranslations';
import { DEFAULT_LOCALE, resolveAppLanguage, type AppLanguage } from '@/utils/i18nUtils';
import { loadTranslationCatalog } from '@/i18n/loadTranslationCatalog';

interface InternationalizationProps {
  children: ReactNode;
}

interface IntlState {
  locale: AppLanguage;
  messages: Record<string, string>;
}

const INITIAL_INTL_STATE: IntlState = {
  locale: DEFAULT_LOCALE,
  messages: {},
};

/**
 * When `crowdinTranslator` is on, loads the approved catalog for the user's account
 * (or browser) locale. When it is off, `FormattedMessage` renders English `defaultMessage`.
 * Decibel `LocaleProvider` stays on `en-US`.
 *
 * Account locale is ignored until GraphQL settles so we do not flash the browser language
 * (and a stale catalog) before `viewer.locale` wins. Locale and messages update together.
 */
export const Internationalization = ({ children }: InternationalizationProps): ReactNode => {
  const { locale: accountLocale, isAccountLocaleReady } = useViewerLocale();
  const areTranslationsEnabled = useTranslations();
  const resolvedLocale = isAccountLocaleReady
    ? resolveAppLanguage(accountLocale)
    : DEFAULT_LOCALE;
  const [intlState, setIntlState] = useState<IntlState>(INITIAL_INTL_STATE);

  useEffect(() => {
    if (!areTranslationsEnabled) {
      setIntlState(INITIAL_INTL_STATE);
      return;
    }

    if (!isAccountLocaleReady) {
      return;
    }

    let isCancelled = false;

    const loadCatalog = async () => {
      const catalog = await loadTranslationCatalog(resolvedLocale);
      if (!isCancelled) {
        setIntlState({ locale: resolvedLocale, messages: catalog });
      }
    };

    void loadCatalog();

    return () => {
      isCancelled = true;
    };
  }, [areTranslationsEnabled, isAccountLocaleReady, resolvedLocale]);

  return (
    <IntlProvider
      locale={intlState.locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={intlState.messages}
      onError={(error) => {
        if (error.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
          return;
        }
        console.error(error);
      }}
    >
      {children}
    </IntlProvider>
  );
};
